import { Close as CloseIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Collapse,
  IconButton,
  Portal,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { APP_PAPER_RADIUS } from '@src/components/common/AppPaper';
import {
  removeToast,
  selectToastSettings,
  selectToasts,
  type Toast,
} from '@src/store/slices/toastSlice';
import type React from 'react';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TransitionGroup } from 'react-transition-group';

/**
 * 個別トーストコンポーネント
 */
const ToastItem: React.FC<{
  toast: Toast;
  onClose: (id: string) => void;
}> = ({ toast, onClose }) => {
  const { defaultDuration } = useSelector(selectToastSettings);

  useEffect(() => {
    if (!toast.persistent) {
      let duration = toast.duration || defaultDuration;

      // エラー以外のメッセージは半分の時間で消える
      if (toast.severity !== 'error' && !toast.duration) {
        duration = defaultDuration / 2;
      }

      const timer = setTimeout(() => {
        onClose(toast.id);
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [toast, defaultDuration, onClose]);

  return (
    <Alert
      severity={toast.severity}
      sx={{
        width: '100%',
        maxWidth: 400,
        boxShadow: 3,
        mb: 1,
        alignItems: 'center',
        display: 'flex',
        borderRadius: APP_PAPER_RADIUS,
        '& .MuiAlert-message': {
          display: 'flex',
          alignItems: 'center',
          width: '100%',
        },
      }}
      action={
        <Stack direction="row" spacing={1} alignItems="center">
          {toast.action && (
            <Button
              size="small"
              color="inherit"
              onClick={() => {
                toast.action?.onClick();
                onClose(toast.id);
              }}
            >
              {toast.action.label}
            </Button>
          )}
          <IconButton
            aria-label="close"
            color="inherit"
            size="small"
            onClick={() => onClose(toast.id)}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      }
    >
      <Typography
        variant="body2"
        sx={{
          display: 'flex',
          alignItems: 'center',
          fontWeight: 'bold',
        }}
      >
        {toast.message}
      </Typography>
    </Alert>
  );
};

/**
 * トースト通知コンテナ
 *
 * アプリケーション全体のトースト通知を管理・表示
 */
export const ToastContainer: React.FC<{
  position?:
    | 'top-left'
    | 'top-center'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-center'
    | 'bottom-right';
}> = ({ position = 'bottom-right' }) => {
  const dispatch = useDispatch();
  const toasts = useSelector(selectToasts);
  const theme = useTheme();
  // モバイル表示判定（md未満はモバイル、タブレット以上はPC表示と同じ）
  const isMobileView = useMediaQuery(theme.breakpoints.down('md'));

  const handleClose = (id: string) => {
    dispatch(removeToast(id));
  };

  // モバイル時はナビゲーション右下に表示
  const effectivePosition = isMobileView ? 'top-right' : position;

  // ナビゲーションの高さ（モバイル時は56px）
  const navHeight = 56;

  // ポジション別のスタイル設定
  const getPositionStyles = () => {
    const styles: React.CSSProperties = {
      position: 'fixed',
      // PDFプレビューモーダル（zIndex: modal + 10000 = 約11300）より上に表示
      zIndex: 15000,
      pointerEvents: 'none',
      // モバイル時は35%小さく表示
      ...(isMobileView && {
        transform: 'scale(0.65)',
        transformOrigin: 'top right',
      }),
    };

    switch (effectivePosition) {
      case 'top-left':
        return {
          ...styles,
          top: isMobileView ? navHeight + 8 : 16,
          left: 16,
          transformOrigin: 'top left',
        };
      case 'top-center':
        return {
          ...styles,
          top: isMobileView ? navHeight + 8 : 16,
          left: '50%',
          transform: isMobileView ? 'translateX(-50%) scale(0.65)' : 'translateX(-50%)',
          transformOrigin: 'top center',
        };
      case 'top-right':
        // モバイル時はナビゲーションの右下に表示
        return { ...styles, top: isMobileView ? navHeight + 8 : 16, right: 16 };
      case 'bottom-left':
        return { ...styles, bottom: 16, left: 16, transformOrigin: 'bottom left' };
      case 'bottom-center':
        return {
          ...styles,
          bottom: 16,
          left: '50%',
          transform: isMobileView ? 'translateX(-50%) scale(0.65)' : 'translateX(-50%)',
          transformOrigin: 'bottom center',
        };
      default:
        return { ...styles, bottom: 16, right: 16, transformOrigin: 'bottom right' };
    }
  };

  const isTopPosition = effectivePosition.includes('top');

  return (
    <Portal>
      <Box sx={getPositionStyles()}>
        <Stack
          spacing={1}
          sx={{
            pointerEvents: 'auto',
            ...(isTopPosition ? {} : { flexDirection: 'column-reverse' }),
          }}
        >
          <TransitionGroup>
            {toasts.map((toast) => (
              <Collapse key={toast.id} timeout={300}>
                <ToastItem toast={toast} onClose={handleClose} />
              </Collapse>
            ))}
          </TransitionGroup>
        </Stack>
      </Box>
    </Portal>
  );
};
